// 引き継ぎコード（クライアント側）。docs/AUTH.md の実装順序(6)。
//
// パスキーが使えない場面の**保険**。主軸はパスキーで、ここは「入り直すための鍵を紙に書ける形」。
//
// ⚠️ **`redeemTransferCode` は localStorage を書き換えない**（[passkey.ts](passkey.ts) の
//    `loginWithPasskey` と同じ約束）。誰として入れたかを返すだけにして、
//    「この端末に残っている記録をどうするか」の判断は呼び出し側に残す。
//    ここで持ち主を差し替えると、統合の確認より先にローカルが別人のものになる。

import type { LoggedInPlayer } from '@/platform/types/session';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

/** コードの文字数（区切りを除いた実体）。サーバーの `TRANSFER_CODE_LEN` と同じ値。 */
export const TRANSFER_CODE_LEN = 16;

export type IssueTransferCodeResult =
  | { ok: true; code: string }
  /**
   * `denied` … この端末では発行できない（既にパスキーがあるのにセッションが無い）。
   *   ⚠️ `failed` に混ぜないこと。**再試行しても永久に成功しない**ので、
   *      「先にログインしてください」と言い切る必要がある（`already` の教訓と同じ）。
   */
  | { ok: false; reason: 'denied' | 'failed' };

/**
 * 引き継ぎコードを発行する。**返ってくる平文はこの1回きり**
 * （サーバーは指紋しか保存しないので、あとから同じコードを取り出せない）。
 *
 * ⚠️ 発行すると**前のコードは無効になる**。押す前に確認を取ること。
 */
export async function issueTransferCode(playerId: string): Promise<IssueTransferCodeResult> {
  try {
    const res = await fetch(`${API_BASE}/auth/transfer-code`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      // 2本目以降のパスキーを持つプレイヤーはセッションで本人確認される。
      credentials: 'same-origin',
      body: JSON.stringify({ playerId }),
    });
    if (res.status === 401) return { ok: false, reason: 'denied' };
    if (!res.ok) return { ok: false, reason: 'failed' };
    const body = (await res.json()) as { code?: unknown };
    return typeof body.code === 'string' && body.code.length > 0
      ? { ok: true, code: body.code }
      : { ok: false, reason: 'failed' };
  } catch {
    return { ok: false, reason: 'failed' };
  }
}

export type RedeemTransferCodeResult =
  | { ok: true; player: LoggedInPlayer }
  /**
   * `malformed` … 桁数や文字が違う（打ち間違い）。
   * `unknown`   … 形は正しいが、そのコードのアカウントが無い（無効・発行しなおされた）。
   *   ※ この2つを分けるのは**画面の文言のためだけ**。形式は公開情報なので、
   *      分けてもコードの実在に関する情報は漏れない。
   */
  | { ok: false; reason: 'malformed' | 'unknown' | 'failed' };

/**
 * 引き継ぎコードで入る。成功するとサーバーがセッション Cookie を返す
 * ＝ **この端末はログイン済みになる**（localStorage はまだ書き換わらない）。
 */
export async function redeemTransferCode(code: string): Promise<RedeemTransferCodeResult> {
  // 形だけは手元で見る（明らかな打ち間違いで往復させない）。判定の正本はサーバー。
  if (!looksLikeTransferCode(code)) return { ok: false, reason: 'malformed' };
  try {
    const res = await fetch(`${API_BASE}/auth/transfer`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ code }),
    });
    if (res.status === 400) return { ok: false, reason: 'malformed' };
    if (res.status === 401) return { ok: false, reason: 'unknown' };
    if (!res.ok) return { ok: false, reason: 'failed' };
    const body = (await res.json()) as { player?: LoggedInPlayer };
    return body.player ? { ok: true, player: body.player } : { ok: false, reason: 'failed' };
  } catch {
    return { ok: false, reason: 'failed' };
  }
}

/**
 * 入力欄の見た目を整える（`XXXX-XXXX-XXXX-XXXX`）。
 * **判定には使わない**（読み替えや検証はサーバーの `normalizeTransferCode` が正本）。
 */
export function formatTransferCodeInput(raw: string): string {
  const body = raw.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, TRANSFER_CODE_LEN);
  return (body.match(/.{1,4}/g) ?? []).join('-');
}

/** 区切りを除いて16文字あるか（往復させる前の足切りだけ）。 */
export function looksLikeTransferCode(raw: string): boolean {
  return raw.replace(/[^0-9A-Za-z]/g, '').length === TRANSFER_CODE_LEN;
}
